/**
 * 数据表格与「加载更多」。
 *
 * 背景：Key、用量明细、流水、订单、审计都是列表；后端统一用游标分页（每页工作量固定，不用 OFFSET）。
 * 设计意图：列定义里给 render 函数，表格本身不关心数据形状；数字列加 num 类右对齐、等宽数字。
 *   分页只提供「加载更多」，与游标分页一致，不做页码跳转。
 * 关键约束：窄屏时表格横向滚动，不折行挤压列。
 */
import type { ReactNode } from 'react'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Button, Empty, Loading } from './primitives.tsx'

export interface Column<T> {
  key: string
  title: ReactNode
  render: (row: T) => ReactNode
  num?: boolean
  nowrap?: boolean
  muted?: boolean
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading,
  empty,
}: {
  columns: Column<T>[]
  rows: T[] | null | undefined
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  loading?: boolean
  empty?: ReactNode
}) {
  if (!rows) return loading ? <Loading /> : null
  if (rows.length === 0) return loading ? <Loading /> : <>{empty ?? <Empty />}</>
  return (
    <div className="ap-table-wrap">
      <table className={`ap-table ${onRowClick ? 'ap-table--click' : ''}`}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.num ? 'num' : undefined}>
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={[c.num && 'num', c.nowrap && 'nowrap', c.muted && 'muted'].filter(Boolean).join(' ') || undefined}
                >
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick: () => void }) {
  const { t } = useI18n()
  if (!hasMore) return null
  return (
    <div className="ap-more">
      <Button size="sm" loading={loading} onClick={onClick}>
        {t(C.loadMore)}
      </Button>
    </div>
  )
}
